import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ConversationListItem } from "@/components/admin/ConversationListItem";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Conversation {
  id: string;
  customer_phone: string;
  customer_name: string | null;
  last_message_at: string;
  unread_count: number;
  status: string;
}

interface PhoneNumber {
  twilio_phone_number: string;
  display_name: string;
}

const SMSPhoneConversations = () => {
  const { user, loading: authLoading } = useAuth();
  const { phoneNumberId } = useParams<{ phoneNumberId: string }>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && phoneNumberId) {
      loadPhoneNumber();
      loadConversations();
      
      // Subscribe to real-time updates
      const channel = supabase
        .channel('conversations-updates')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'sms_conversations',
          filter: `phone_number_id=eq.${phoneNumberId}`
        }, () => {
          loadConversations();
        })
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'sms_messages'
        }, () => {
          loadConversations();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, phoneNumberId]);

  const loadPhoneNumber = async () => {
    try {
      const { data, error } = await supabase
        .from('phone_numbers')
        .select('twilio_phone_number, display_name')
        .eq('id', phoneNumberId)
        .single();

      if (error) throw error;
      setPhoneNumber(data);
    } catch (error) {
      console.error('Error loading phone number:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_conversations')
        .select('*')
        .eq('phone_number_id', phoneNumberId)
        .eq('status', 'active')
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  const formattedPhone = phoneNumber?.twilio_phone_number.replace(
    /^\+?1?(\d{3})(\d{3})(\d{4})$/,
    '($1) $2-$3'
  );

  return (
    <>
      <SEOHead 
        title={`Conversations - ${phoneNumber?.display_name || 'Phone Number'}`}
        description="View and manage SMS conversations"
      />
      
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/admin/sms-phone-numbers">
            <Button variant="ghost" className="mb-4 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Phone Numbers
            </Button>
          </Link>
          
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {formattedPhone || 'Loading...'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {phoneNumber?.display_name}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No conversations found
          </div>
        ) : (
          <div className="grid gap-2">
            {conversations.map((conversation) => (
              <ConversationListItem 
                key={conversation.id} 
                conversation={conversation}
                phoneNumberId={phoneNumberId!}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SMSPhoneConversations;
